import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import { useTranslation } from "react-i18next";

interface OverflowCellProps {
  notifId: number | string;
  values: any[];
  highlightText: (text: string) => string;
  label?: "classes" | "regulators";
}

const OverflowCell: React.FC<OverflowCellProps> = ({
  notifId,
  values,
  highlightText,
  label,
}) => {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const check = () => setIsTruncated(el.scrollHeight > el.clientHeight + 1);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [values]);

  if (!Array.isArray(values) || values.length === 0) {
    return label === "classes" ? <>{t("notification.allClasses")}</> : <>-</>;
  }

  const tooltipClass = `tooltip-cell-${notifId}`;

  return (
    <>
      <div
        ref={ref}
        className={`text-sm line-clamp-4 overflow-hidden text-slate-600 dark:text-slate-300 ${
          isTruncated ? tooltipClass : ""
        }`}
        dangerouslySetInnerHTML={{
          __html: values
            .map((v: any) =>
              highlightText(typeof v === "string" ? v : v.name || ""),
            )
            .join("<br/>"),
        }}
      />
      {isTruncated && (
        <Tooltip
          anchorSelect={`.${tooltipClass}`}
          place="top"
          positionStrategy="fixed"
          className="text-xs font-light z-50"
          content={values
            .map((v: any) => (typeof v === "string" ? v : v.name || ""))
            .join(", ")}
          style={{
            maxWidth: "400px",
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        />
      )}
    </>
  );
};

export { OverflowCell };
